import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, KeyRound, Users2, LogOut, ShieldCheck,
  UserPlus, Trash2, Crown, User, ShieldAlert, Mail, RefreshCw, History,
  Globe, Ban, CheckCircle2, MonitorSmartphone, BellRing, Bell,
} from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_META = {
  owner:        { label_es: "Owner",   label_en: "Owner",   color: "bg-canales-yellow text-black", Icon: Crown },
  admin:        { label_es: "Admin (Completo)", label_en: "Admin (Full)",  color: "bg-canales-blue text-white",   Icon: ShieldCheck },
  "admin-lite": { label_es: "Admin (Solo Lectura)", label_en: "Admin (Read-only)", color: "bg-canales-cream text-black", Icon: ShieldAlert },
  seller:       { label_es: "Vendedor",label_en: "Seller",  color: "bg-neutral-200 text-black",    Icon: User },
  manager:      { label_es: "Manager", label_en: "Manager", color: "bg-canales-blue/70 text-white", Icon: User },
};

// Zero-dep user-agent parser — enough signal to spot an intruder without
// pulling a big library. Returns `{ browser, os, device }`.
const parseUA = (ua = "") => {
  const s = String(ua);
  const browser =
    /Edg\//.test(s)         ? "Edge" :
    /OPR\//.test(s)         ? "Opera" :
    /Chrome\//.test(s)      ? "Chrome" :
    /Firefox\//.test(s)     ? "Firefox" :
    /Safari\//.test(s) && !/Chrome/.test(s) ? "Safari" :
    /WhatsApp/i.test(s)     ? "WhatsApp bot" :
    /facebookexternalhit|Slackbot|TelegramBot|iMessage/i.test(s) ? "Preview bot" :
    /curl|wget|httpx|python/i.test(s) ? "curl / script" :
    "?";
  const os =
    /Windows NT/.test(s)    ? "Windows" :
    /Mac OS X/.test(s)      ? "macOS" :
    /Android/.test(s)       ? "Android" :
    /iPhone|iPad|iOS/.test(s) ? "iOS" :
    /Linux/.test(s)         ? "Linux" :
    "?";
  const device =
    /Mobi|Android|iPhone/.test(s) ? "Mobile" : "Desktop";
  return { browser, os, device };
};

const REJECT_REASONS = {
  not_allowed: { es: "Email no autorizado", en: "Email not allowed", color: "bg-canales-red text-white" },
  no_email:    { es: "Sin email", en: "No email", color: "bg-neutral-700 text-white" },
  domain_not_allowed: { es: "Dominio bloqueado", en: "Domain blocked", color: "bg-canales-red text-white" },
};

export default function AdminSettings() {
  const { lang } = useLang();
  const { user: me } = useAuth();

  // Passcode
  const [status, setStatus] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busyPw, setBusyPw] = useState(false);

  // Users / roles
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Whitelist
  const [whitelist, setWhitelist] = useState([]);
  const [wlEmail, setWlEmail] = useState("");
  const [wlNote, setWlNote] = useState("");
  const [busyWl, setBusyWl] = useState(false);

  // Audit log
  const [audit, setAudit] = useState([]);

  // Access history (login attempts)
  const [access, setAccess] = useState({ items: [], stats: null });
  const [accessFilter, setAccessFilter] = useState("all"); // all | success | rejected
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Blocked IPs + owner notifications (anti-brute-force)
  const [blocks, setBlocks] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });

  const loadBlocks = async () => {
    try {
      const [b, n] = await Promise.all([
        api.get("/admin/blocked-ips").catch(() => ({ data: [] })),
        api.get("/admin/notifications").catch(() => ({ data: { items: [], unread: 0 } })),
      ]);
      setBlocks(b.data || []);
      setNotifs(n.data || { items: [], unread: 0 });
    } catch (_) { /* handled by fallbacks */ }
  };
  useEffect(() => { loadBlocks(); }, []);

  const unblockIp = async (ip) => {
    if (!window.confirm(
      lang === "es" ? `¿Desbloquear ${ip}?` : `Unblock ${ip}?`
    )) return;
    try {
      await api.delete(`/admin/blocked-ips/${encodeURIComponent(ip)}`);
      toast.success(lang === "es" ? "IP desbloqueada" : "IP unblocked");
      loadBlocks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const dismissNotifs = async () => {
    try {
      await api.post("/admin/notifications/mark-read", { notif_ids: null });
      loadBlocks();
    } catch (_) {}
  };

  const loadAccess = async (only = accessFilter) => {
    setLoadingAccess(true);
    try {
      const { data } = await api.get(`/admin/access-log?only=${only}&limit=200`);
      setAccess(data || { items: [], stats: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoadingAccess(false);
    }
  };
  useEffect(() => { loadAccess(accessFilter); }, [accessFilter]);

  const loadAll = async () => {
    try {
      const [s, u, w, a] = await Promise.all([
        api.get("/admin/gate-password/status").catch(() => ({ data: null })),
        api.get("/admin/users").catch(() => ({ data: [] })),
        api.get("/admin/whitelist").catch(() => ({ data: [] })),
        api.get("/admin/audit").catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setUsers(u.data || []);
      setWhitelist(w.data || []);
      setAudit(a.data || []);
    } finally {
      setLoadingUsers(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const rotate = async (e) => {
    e.preventDefault();
    if (pw.length < 4) return toast.error(lang === "es" ? "Mínimo 4 caracteres" : "At least 4 chars");
    if (pw !== pw2) return toast.error(lang === "es" ? "Las contraseñas no coinciden" : "Passwords don't match");
    setBusyPw(true);
    try {
      await api.post("/admin/gate-password", { password: pw });
      toast.success(lang === "es" ? "Contraseña actualizada. Todos deberán volver a entrar." : "Password rotated.");
      setPw(""); setPw2("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusyPw(false); }
  };

  const changeRole = async (userItem, role) => {
    if (userItem.role === role) return;
    if (role === "owner" && me?.role !== "owner") {
      return toast.error(lang === "es" ? "Solo el owner puede otorgar rol owner" : "Only the owner can grant owner role");
    }
    if (userItem.email === me?.email && userItem.role === "owner" && role !== "owner") {
      if (!window.confirm(lang === "es"
        ? "⚠️ Estás quitándote a ti mismo del rol owner. ¿Continuar?"
        : "⚠️ You're removing yourself from the owner role. Continue?"
      )) return;
    }
    try {
      await api.put(`/admin/users/${userItem.user_id}/role`, { role });
      toast.success(lang === "es" ? "Rol actualizado" : "Role updated");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const addWhitelist = async (e) => {
    e.preventDefault();
    const email = wlEmail.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return toast.error(lang === "es" ? "Email inválido" : "Invalid email");
    }
    setBusyWl(true);
    try {
      await api.post("/admin/whitelist", { email, note: wlNote });
      toast.success(lang === "es" ? "Email autorizado" : "Email whitelisted");
      setWlEmail(""); setWlNote("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusyWl(false); }
  };

  const removeWhitelist = async (email) => {
    if (!window.confirm(lang === "es" ? `¿Quitar acceso a ${email}?` : `Revoke ${email}?`)) return;
    try {
      await api.delete(`/admin/whitelist/${encodeURIComponent(email)}`);
      toast.success(lang === "es" ? "Acceso revocado" : "Access revoked");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 max-w-5xl">
      <div>
        <h1 className="font-display font-black text-4xl flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-canales-red" />
          {lang === "es" ? "Configuración" : "Settings"}
        </h1>
        <p className="font-body text-neutral-600 mt-1">
          {lang === "es"
            ? "Contraseña de acceso, usuarios y whitelist de correos externos autorizados."
            : "App passcode, users, and external email whitelist."}
        </p>
      </div>

      {/* ============== PASSCODE ============== */}
      <section className="space-y-3">
        <h2 className="font-display font-black text-2xl flex items-center gap-2">
          <Lock className="w-6 h-6 text-canales-blue" />
          {lang === "es" ? "Contraseña de la tienda" : "Store passcode"}
        </h2>
        {status && (
          <div className="brutal p-4 bg-white flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-display font-bold">
                {status.updated_at
                  ? `${lang === "es" ? "Actualizada" : "Updated"} ${new Date(status.updated_at).toLocaleString()}`
                  : (lang === "es" ? "Usando contraseña por defecto — cámbiala." : "Using default password — please rotate.")}
              </div>
            </div>
            <div className="brutal-sm bg-canales-yellow px-3 py-1 font-display font-bold text-sm flex items-center gap-1">
              <Users2 className="w-4 h-4" /> {status.active_sessions}
              <span className="opacity-70">{lang === "es" ? "sesiones" : "sessions"}</span>
            </div>
          </div>
        )}
        <form onSubmit={rotate} className="brutal p-5 bg-white space-y-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-gate-form">
          <div>
            <Label>{lang === "es" ? "Nueva contraseña" : "New password"}</Label>
            <Input data-testid="admin-gate-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" />
          </div>
          <div>
            <Label>{lang === "es" ? "Confirmar" : "Confirm"}</Label>
            <Input data-testid="admin-gate-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={busyPw} data-testid="admin-gate-submit" className="brutal-btn bg-canales-red text-white px-4 py-2 flex items-center gap-1">
              <KeyRound className="w-4 h-4" />
              {lang === "es" ? "Guardar y cerrar todas las sesiones" : "Save & close all sessions"}
            </Button>
          </div>
        </form>
      </section>

      {/* ============== USERS & ROLES ============== */}
      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display font-black text-2xl flex items-center gap-2">
            <Users2 className="w-6 h-6 text-canales-blue" />
            {lang === "es" ? "Usuarios y Roles" : "Users & Roles"}
          </h2>
          <button
            onClick={loadAll}
            data-testid="admin-users-refresh"
            className="brutal-btn bg-white px-3 py-1 text-sm flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> {lang === "es" ? "Refrescar" : "Refresh"}
          </button>
        </div>
        <p className="text-xs font-body text-neutral-600">
          {lang === "es"
            ? "Owner puede otorgar cualquier rol. Admins pueden bajar a Vendedor o subir a Admin (no a Owner)."
            : "Owner can grant any role. Admins can demote to Seller or promote to Admin (not Owner)."}
        </p>
        <div className="brutal bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canales-cream">
              <tr>
                <th className="text-left p-3 font-display font-black">{lang === "es" ? "Email" : "Email"}</th>
                <th className="text-left p-3 font-display font-black">{lang === "es" ? "Nombre" : "Name"}</th>
                <th className="text-left p-3 font-display font-black">{lang === "es" ? "Proveedor" : "Provider"}</th>
                <th className="text-left p-3 font-display font-black">Rol</th>
                <th className="text-left p-3 font-display font-black"></th>
              </tr>
            </thead>
            <tbody>
              {loadingUsers && (
                <tr><td colSpan={5} className="text-center text-neutral-500 py-4">…</td></tr>
              )}
              {!loadingUsers && users.length === 0 && (
                <tr><td colSpan={5} className="text-center text-neutral-500 py-4">{lang === "es" ? "Sin usuarios" : "No users"}</td></tr>
              )}
              {users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.seller;
                const RoleIcon = meta.Icon;
                const isMe = u.email === me?.email;
                return (
                  <tr key={u.user_id} data-testid={`admin-user-row-${u.email}`} className="border-t-2 border-black/10">
                    <td className="p-3 font-mono text-xs">
                      {u.email}
                      {isMe && <span className="ml-2 inline-block bg-canales-red text-white text-[10px] font-display font-black px-1.5 py-0.5 rounded">{lang === "es" ? "TÚ" : "YOU"}</span>}
                    </td>
                    <td className="p-3 font-body">{u.name || "—"}</td>
                    <td className="p-3 font-body text-xs uppercase tracking-widest">{u.provider || "google"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-black text-xs font-display font-bold ${meta.color}`}>
                        <RoleIcon className="w-3 h-3" /> {meta[`label_${lang}`]}
                      </span>
                    </td>
                    <td className="p-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger data-testid={`admin-role-select-${u.email}`} className="w-40 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="seller">{ROLE_META.seller[`label_${lang}`]}</SelectItem>
                          <SelectItem value="admin-lite">{ROLE_META["admin-lite"][`label_${lang}`]}</SelectItem>
                          <SelectItem value="admin">{ROLE_META.admin[`label_${lang}`]}</SelectItem>
                          <SelectItem value="owner" disabled={me?.role !== "owner"}>{ROLE_META.owner[`label_${lang}`]}</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ============== EMAIL WHITELIST (external guests) ============== */}
      <section className="space-y-3">
        <h2 className="font-display font-black text-2xl flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-canales-red" />
          {lang === "es" ? "Emails autorizados (Admin)" : "Authorized admin emails"}
        </h2>
        <div className="brutal-sm bg-canales-yellow p-3 text-sm font-body">
          {lang === "es" ? (
            <>
              <b>Importante:</b> solo los emails aquí (más el Owner Tony) pueden
              entrar al Admin Panel con Google o Microsoft. Cualquier otro correo
              intentando iniciar sesión será rechazado con <code>403</code>.
              Quitar un email de esta lista revoca su acceso <b>inmediatamente</b>
              en el siguiente intento de login.
            </>
          ) : (
            <>
              <b>Important:</b> only the emails below (plus the Owner) may sign
              into the Admin Panel via Google or Microsoft. Any other email
              attempting to log in is rejected with <code>403</code>. Removing
              an email revokes access on the next login attempt.
            </>
          )}
        </div>
        <p className="text-xs font-body text-neutral-600">
          {lang === "es"
            ? "Agrega el email personal de cada admin autorizado — sin importar dominio corporativo."
            : "Add the personal email of each authorized admin — corporate domain not required."}
        </p>
        <form onSubmit={addWhitelist} className="brutal p-4 bg-white grid grid-cols-1 md:grid-cols-[2fr_2fr_auto] gap-2 items-end" data-testid="admin-whitelist-form">
          <div>
            <Label>Email</Label>
            <Input
              data-testid="admin-whitelist-email"
              type="email"
              value={wlEmail}
              onChange={(e) => setWlEmail(e.target.value)}
              placeholder="consultor@empresa.com"
            />
          </div>
          <div>
            <Label>{lang === "es" ? "Nota (opcional)" : "Note (optional)"}</Label>
            <Input
              data-testid="admin-whitelist-note"
              value={wlNote}
              onChange={(e) => setWlNote(e.target.value)}
              placeholder={lang === "es" ? "Consultor externo autorizado por Tony" : "External consultant"}
            />
          </div>
          <Button
            type="submit"
            disabled={busyWl}
            data-testid="admin-whitelist-add"
            className="brutal-btn bg-canales-red text-white px-4 py-2 flex items-center gap-1"
          >
            <UserPlus className="w-4 h-4" /> {lang === "es" ? "Autorizar" : "Add"}
          </Button>
        </form>
        <div className="brutal bg-white">
          {whitelist.length === 0 ? (
            <div className="p-4 text-center font-body text-neutral-500 text-sm">
              {lang === "es"
                ? "Solo el Owner puede entrar. Agrega el email de cada admin autorizado arriba."
                : "Only the Owner may enter. Add each authorized admin above."}
            </div>
          ) : (
            <ul className="divide-y-2 divide-black/10">
              {whitelist.map((w) => (
                <li key={w.email} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`admin-whitelist-row-${w.email}`}>
                  <Mail className="w-4 h-4 text-neutral-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm">{w.email}</div>
                    {w.note && <div className="text-xs font-body text-neutral-600">{w.note}</div>}
                  </div>
                  <div className="text-xs font-body text-neutral-500">
                    {w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}
                  </div>
                  <button
                    onClick={() => removeWhitelist(w.email)}
                    className="brutal-btn bg-red-500 text-white px-2 py-1 text-sm flex items-center gap-1"
                    data-testid={`admin-whitelist-remove-${w.email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ============== OWNER NOTIFICATIONS BANNER ============== */}
      <section
        data-testid="admin-notifs-section"
        className="space-y-2"
      >
        <h2 className="font-display font-black text-2xl flex items-center gap-2">
          <BellRing className={`w-6 h-6 ${notifs.unread > 0 ? "text-canales-red animate-pulse" : "text-canales-blue"}`} />
          {lang === "es" ? "Alertas de seguridad" : "Security alerts"}
        </h2>
        {notifs.unread > 0 ? (
          <div
            data-testid="admin-notifs-banner"
            className="brutal bg-canales-red text-white p-4 flex flex-wrap items-start gap-3"
          >
            <BellRing className="w-6 h-6 shrink-0 mt-0.5 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-lg">
                {lang === "es"
                  ? `${notifs.unread} alerta${notifs.unread === 1 ? "" : "s"} sin leer`
                  : `${notifs.unread} unread alert${notifs.unread === 1 ? "" : "s"}`}
              </div>
              <ul className="mt-1 text-sm font-body space-y-1">
                {notifs.items.filter((n) => !n.read).slice(0, 5).map((n) => (
                  <li key={n.notif_id} className="flex items-start gap-2">
                    <span className="text-white/80">·</span>
                    <div>
                      <div>{n.message}</div>
                      <div className="text-xs text-white/70">
                        {n.ts ? new Date(n.ts).toLocaleString() : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={dismissNotifs}
              data-testid="admin-notifs-dismiss"
              className="brutal-sm px-3 py-1 bg-white text-canales-red text-xs font-display font-black inline-flex items-center gap-1"
            >
              <Bell className="w-3 h-3" />
              {lang === "es" ? "Marcar como leídas" : "Mark as read"}
            </button>
          </div>
        ) : (
          <div
            data-testid="admin-notifs-empty"
            className="brutal bg-white p-4 flex items-center gap-3 text-sm font-body text-neutral-600"
          >
            <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0" />
            <span>
              {lang === "es"
                ? "Sin alertas nuevas. Cuando el sistema bloquee automáticamente una IP por intentos fallidos, aparecerá aquí."
                : "No new alerts. Auto-blocked IPs from brute-force attempts will show up here."}
            </span>
          </div>
        )}
      </section>

      {/* ============== BLOCKED IPs ============== */}
      <section className="space-y-3" data-testid="admin-blocked-ips">
        <h2 className="font-display font-black text-2xl flex items-center gap-2">
          <Ban className="w-6 h-6 text-canales-red" />
          {lang === "es" ? "IPs bloqueadas" : "Blocked IPs"}
        </h2>
        <p className="text-xs font-body text-neutral-600">
          {lang === "es"
            ? "Bloqueo automático tras 5 intentos rechazados en 15 min. Cada bloqueo dura 1 hora. Puedes desbloquear manualmente si sabes que la IP es tuya."
            : "Auto-block after 5 rejected attempts in 15 min. Each block lasts 1 hour. Manually unblock if you know the IP is yours."}
        </p>
        <div className="brutal bg-white">
          {blocks.length === 0 ? (
            <div className="p-6 text-center text-neutral-500 text-sm font-body">
              {lang === "es"
                ? "Ninguna IP está bloqueada ahora mismo. 🎉"
                : "No IPs currently blocked. 🎉"}
            </div>
          ) : (
            <ul className="divide-y-2 divide-black/10">
              {blocks.map((b) => (
                <li
                  key={b.ip}
                  data-testid={`blocked-ip-row-${b.ip}`}
                  className={`p-3 flex flex-wrap items-center gap-3 ${b.is_active ? "bg-canales-red/5" : "opacity-60"}`}
                >
                  <div className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full border-2 border-black ${b.is_active ? "bg-canales-red text-white" : "bg-neutral-300 text-neutral-700"}`}>
                    <Ban className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm font-bold">{b.ip}</div>
                    <div className="text-xs font-body text-neutral-600">
                      {lang === "es" ? "Motivo" : "Reason"}: <span className="font-mono">{b.reason}</span>
                      {" · "}{b.attempts_in_window || 0}{" "}
                      {lang === "es" ? "intentos" : "attempts"}
                      {b.last_email_tried && (
                        <> · {lang === "es" ? "último email" : "last email"}: <span className="font-mono">{b.last_email_tried}</span></>
                      )}
                    </div>
                    <div className="text-xs font-body text-neutral-500">
                      {b.blocked_at ? new Date(b.blocked_at).toLocaleString() : ""}
                      {b.is_active && b.expires_in_seconds > 0 && (
                        <> · {lang === "es" ? "expira en" : "expires in"} {" "}
                          <span className="font-display font-bold text-canales-red">
                            {Math.ceil(b.expires_in_seconds / 60)} min
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {b.is_active && (
                    <button
                      onClick={() => unblockIp(b.ip)}
                      data-testid={`blocked-ip-unblock-${b.ip}`}
                      className="brutal-sm px-3 py-1 bg-canales-yellow text-black text-xs font-display font-black inline-flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      {lang === "es" ? "Desbloquear" : "Unblock"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ============== ACCESS HISTORY (login attempts) ============== */}
      <section className="space-y-3" data-testid="admin-access-history">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-display font-black text-2xl flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-canales-red" />
            {lang === "es" ? "Historial de accesos" : "Access history"}
          </h2>
          <button
            onClick={() => loadAccess(accessFilter)}
            className="brutal-sm px-2 py-1 bg-white text-xs font-display font-bold inline-flex items-center gap-1"
            data-testid="access-refresh"
            title={lang === "es" ? "Recargar" : "Refresh"}
          >
            <RefreshCw className={`w-3 h-3 ${loadingAccess ? "animate-spin" : ""}`} />
            {lang === "es" ? "Refrescar" : "Refresh"}
          </button>
        </div>
        <p className="text-xs font-body text-neutral-600">
          {lang === "es"
            ? "Cada intento de login (Google / Microsoft) queda registrado con IP y navegador. Ideal para detectar quién intentó colarse."
            : "Every OAuth login attempt (Google / Microsoft) is logged with IP and browser. Use this to spot intrusion attempts."}
        </p>

        {/* Stats chips */}
        {access.stats && (
          <div className="flex flex-wrap gap-2 text-xs font-display font-black uppercase tracking-widest" data-testid="access-stats">
            <span className="brutal-sm px-3 py-1 bg-white">
              {lang === "es" ? "Total" : "Total"}: {access.stats.total}
            </span>
            <span className="brutal-sm px-3 py-1 bg-green-600 text-white inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {lang === "es" ? "Éxito" : "Success"}: {access.stats.success}
            </span>
            <span className="brutal-sm px-3 py-1 bg-canales-red text-white inline-flex items-center gap-1">
              <Ban className="w-3 h-3" />
              {lang === "es" ? "Rechazado" : "Rejected"}: {access.stats.rejected}
            </span>
            <span className="brutal-sm px-3 py-1 bg-canales-blue text-white inline-flex items-center gap-1">
              <Globe className="w-3 h-3" />
              IPs: {access.stats.unique_ips}
            </span>
            <span className="brutal-sm px-3 py-1 bg-canales-yellow">
              {lang === "es" ? "Emails únicos" : "Unique emails"}: {access.stats.unique_emails}
            </span>
            <span className="brutal-sm px-3 py-1 bg-canales-cream">
              {lang === "es" ? "Hoy" : "Today"}: {access.stats.today}
            </span>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2" role="tablist">
          {[
            { k: "all", label_es: "Todos", label_en: "All" },
            { k: "success", label_es: "Éxito", label_en: "Success" },
            { k: "rejected", label_es: "Rechazados", label_en: "Rejected" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setAccessFilter(t.k)}
              data-testid={`access-tab-${t.k}`}
              className={`brutal-sm px-3 py-1 text-xs font-display font-black uppercase tracking-widest ${
                accessFilter === t.k
                  ? "bg-canales-red text-white"
                  : "bg-white text-neutral-700"
              }`}
            >
              {t[`label_${lang}`]}
            </button>
          ))}
        </div>

        {/* Event list */}
        <div className="brutal bg-white">
          {loadingAccess ? (
            <div className="p-6 text-center text-neutral-500 text-sm font-body">
              {lang === "es" ? "Cargando…" : "Loading…"}
            </div>
          ) : access.items.length === 0 ? (
            <div className="p-6 text-center text-neutral-500 text-sm font-body">
              {lang === "es"
                ? "Sin intentos registrados todavía. Un intruso probando su suerte aparecerá aquí."
                : "No attempts yet. Any intruder trying their luck will show up here."}
            </div>
          ) : (
            <ul className="divide-y-2 divide-black/10">
              {access.items.map((a) => {
                const isReject = a.action === "admin_login_rejected";
                const meta = a.meta || {};
                const ua = parseUA(meta.ua || "");
                const reason = REJECT_REASONS[meta.reason];
                return (
                  <li
                    key={a.audit_id}
                    data-testid={`access-row-${a.audit_id}`}
                    className={`p-3 flex flex-wrap items-center gap-3 ${
                      isReject ? "bg-canales-red/5" : ""
                    }`}
                  >
                    {/* Success/reject glyph */}
                    <div className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full border-2 border-black ${
                      isReject ? "bg-canales-red text-white" : "bg-green-600 text-white"
                    }`}>
                      {isReject ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    </div>

                    {/* Email + provider */}
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm truncate">
                        {a.actor_email || <span className="italic text-neutral-500">(sin email)</span>}
                        {" · "}
                        <span className="text-xs uppercase tracking-widest font-display font-bold text-neutral-600">
                          {meta.provider || "google"}
                        </span>
                      </div>
                      <div className="text-xs font-body text-neutral-500">
                        {a.ts ? new Date(a.ts).toLocaleString() : "—"}
                      </div>
                    </div>

                    {/* IP */}
                    <div className="brutal-sm px-2 py-1 bg-canales-yellow font-mono text-xs inline-flex items-center gap-1" title={lang === "es" ? "Dirección IP" : "IP address"}>
                      <Globe className="w-3 h-3" />
                      {meta.ip || "?"}
                    </div>

                    {/* Browser + OS + device */}
                    <div className="brutal-sm px-2 py-1 bg-white font-mono text-xs inline-flex items-center gap-1" title={meta.ua || ""}>
                      <MonitorSmartphone className="w-3 h-3" />
                      {ua.browser} · {ua.os} · {ua.device}
                    </div>

                    {/* Reject reason chip */}
                    {isReject && (
                      <span className={`brutal-sm px-2 py-1 text-xs font-display font-black uppercase tracking-widest ${reason?.color || "bg-canales-red text-white"}`}>
                        {reason ? reason[lang] : (meta.reason || "reject")}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* ============== AUDIT LOG ============== */}
      <section className="space-y-3">
        <h2 className="font-display font-black text-2xl flex items-center gap-2">
          <History className="w-6 h-6 text-canales-blue" />
          {lang === "es" ? "Historial de cambios" : "Audit log"}
        </h2>
        <p className="text-xs font-body text-neutral-600">
          {lang === "es"
            ? "Últimas 100 acciones: quién cambió qué rol y qué correos autorizó o revocó."
            : "Last 100 actions: who changed which role and which emails were whitelisted or revoked."}
        </p>
        <div className="brutal bg-white overflow-x-auto">
          {audit.length === 0 ? (
            <div className="p-4 text-center font-body text-neutral-500 text-sm">
              {lang === "es" ? "Sin cambios registrados aún" : "No changes recorded yet"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-canales-cream">
                <tr>
                  <th className="text-left p-3 font-display font-black">{lang === "es" ? "Fecha" : "Date"}</th>
                  <th className="text-left p-3 font-display font-black">{lang === "es" ? "Actor" : "Actor"}</th>
                  <th className="text-left p-3 font-display font-black">{lang === "es" ? "Acción" : "Action"}</th>
                  <th className="text-left p-3 font-display font-black">{lang === "es" ? "Objetivo" : "Target"}</th>
                  <th className="text-left p-3 font-display font-black">{lang === "es" ? "Detalle" : "Detail"}</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => {
                  const actionMeta = {
                    role_change: { label_es: "Cambio de rol", label_en: "Role change", bg: "bg-canales-blue text-white" },
                    whitelist_add: { label_es: "Autorizado", label_en: "Whitelisted", bg: "bg-green-500 text-white" },
                    whitelist_remove: { label_es: "Revocado", label_en: "Revoked", bg: "bg-red-500 text-white" },
                  }[a.action] || { label_es: a.action, label_en: a.action, bg: "bg-neutral-200 text-black" };
                  return (
                    <tr key={a.audit_id} className="border-t-2 border-black/10" data-testid={`audit-row-${a.audit_id}`}>
                      <td className="p-3 text-xs font-mono">
                        {a.ts ? new Date(a.ts).toLocaleString() : "—"}
                      </td>
                      <td className="p-3 text-xs font-mono">{a.actor_email}</td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full border-2 border-black text-xs font-display font-bold ${actionMeta.bg}`}>
                          {actionMeta[`label_${lang}`]}
                        </span>
                      </td>
                      <td className="p-3 text-xs font-mono break-all">{a.target}</td>
                      <td className="p-3 text-xs font-body text-neutral-700">
                        {a.action === "role_change" && a.meta && (
                          <>
                            <span className="font-mono bg-neutral-200 px-1 rounded">{a.meta.from}</span>
                            {" → "}
                            <span className="font-mono bg-canales-yellow px-1 rounded">{a.meta.to}</span>
                          </>
                        )}
                        {a.action === "whitelist_add" && a.meta?.note && (
                          <span className="italic">&ldquo;{a.meta.note}&rdquo;</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </motion.div>
  );
}
